/**
 * stages.ts — the CLIENT mirror of clinic-service's deal-stages.ts
 * (2026-07-21 CRM pipeline). AFFORDANCE ONLY (R41: the UI is never the
 * control) — the server validates every move; this mirror exists so the
 * board can grey out illegal drags, the status map can render the allowed
 * targets, and each move routes to the endpoint that owns it:
 *   'stage-patch' → PATCH /deals/:id/stage (pure move; reason for closed_lost)
 *   'handover'    → POST /deals/:id/handover (required note; syncs the ticket)
 *   'convert'     → POST /deals/:id/convert-to-callback (mints a REAL ticket)
 * `closed_won` is SYSTEM-ONLY (padlocked on the status map) — it moves only via
 * orders-service's order-sync. So is an AUTHORISED card in `clinical_review`,
 * but that one is a ROW-STATE rule rather than a stage rule — see
 * `isAwaitingClinicalDecision` below.
 *
 * DEDICATED-ACTION MOVES have no MANUAL_TARGETS entry for the move IN, because
 * their from-stage has no manual targets at all: `POST /deals/:id/recover`
 * (closed_lost → active), `POST /deals/:id/pending-admin-review` (any open
 * stage → pending_admin_review, the clinician→admin handoff) and
 * `POST /deals/:id/mark-duplicate` (any stage ↔ closed_duplicate, both
 * directions). Each is its own control on the deal drawer; MANUAL_TARGETS still
 * describes the moves OUT.
 *
 * ── 2026-09-05/06 STAGE RESTRUCTURE (CTO), MIRRORED HERE ───────────────────
 * clinic-service merged and renamed two lanes, and this file had drifted onto
 * a vocabulary the server no longer has. A portal naming a deleted stage is not
 * a cosmetic problem: its lane never fills, its filter matches nothing, and its
 * drag targets are refused by the server.
 *
 *   eligibility_in_progress  ─┐
 *                             ├─→  clinical_review   (MERGED)
 *   awaiting_clinical_review ─┘
 *   marketing_funnel          ──→  unreachable       (RENAMED)
 *   closed_duplicate                                 (NEW, terminal)
 *
 * The server remaps stored rows in place (LEGACY_STAGE_REMAP + a migration), so
 * there is no transitional period in which the portal must understand both
 * vocabularies — the board only ever sees the new values.
 */
import type { DealCard, DealEntryPath, DealStage, UserRole } from '../api/types';

/** Board display order — the same order as the server's DEAL_STAGES. */
export const DEAL_STAGES: readonly DealStage[] = [
  'mql',
  'callback_requested',
  // THE LEAD LANE (2026-09-07, CTO). A completed questionnaire with NO
  // authorised card. It sits immediately before `clinical_review` because that
  // is the lane it now advances into — the same positioning rule `plan_sent`
  // follows. Mirrors the server's DEAL_STAGES exactly.
  'warm_lead',
  // The merged clinical lane (2026-09-05). It sits where
  // `eligibility_in_progress` sat — third, immediately before `plan_sent` —
  // because that is still the stage `plan_sent` advances from most often.
  'clinical_review',
  // 2026-08-26 (CTO): the clinician approved and the plan invite went out —
  // the deal waits on the PATIENT. Entered automatically by the server's
  // handover hook; sits right after the stage it advances from.
  'plan_sent',
  'awaiting_clinician_followup',
  'awaiting_admin_followup',
  'pending_admin_review',
  'closed_won',
  // Renamed from `marketing_funnel` (2026-09-05). Still after `closed_won`,
  // where the old lane sat — the rename changed what the column is CALLED, not
  // where a card that stalled belongs on the board.
  'unreachable',
  'closed_lost',
  // MERGED DUPLICATES (2026-09-06, CTO — the Asana model). LAST in board order
  // on purpose: after both real outcomes, out of the way of everything staff
  // work. The lane is how a merged card stays REACHABLE — clinic-service hides
  // duplicates from the working lanes but serves them here, so without the
  // column a card that was merged away could not be opened from the board at
  // all.
  'closed_duplicate',
];

/** Simplified display names (the CTO's "simplified names, proper indicators"). */
export const STAGE_LABEL: Record<DealStage, string> = {
  mql: 'New lead',
  callback_requested: 'Callback',
  /**
   * ⚠️ THE LABEL HAS TO NAME THE MISSING CARD, not just say "lead". Everything
   * an admin does with this column depends on knowing WHY the person is in it:
   * they finished the questionnaire and saw their plan, and the only thing they
   * did not do is authorise payment. "Warm lead" alone would sit next to "New
   * lead" reading as a vaguer version of the same thing, and the whole point of
   * the stage is that the two are different populations.
   */
  warm_lead: 'Warm lead — no card yet',
  // ONE LABEL FOR THE MERGED LANE, and it is the clinical one. "Checking
  // eligibility" (the old `eligibility_in_progress` label) described the
  // patient's half of the work; this column is now defined by whose queue it is
  // — a clinician's — whether or not the patient has paid yet. The paid/unpaid
  // distinction that used to be carried by two column names is carried by the
  // card's own facts instead (an order behind it, the Review tab).
  clinical_review: 'Clinical review',
  plan_sent: 'Plan sent — awaiting patient',
  awaiting_clinician_followup: 'Clinician follow-up',
  awaiting_admin_followup: 'Admin follow-up',
  pending_admin_review: 'Pending admin review',
  closed_won: 'Won',
  /**
   * ⚠️ NOT "Marketing funnel", AND THE LABEL IS THE WHOLE REASON THE STAGE WAS
   * RENAMED. A card lands here after 3 clinician call attempts and 3 admin call
   * attempts have all gone unanswered — these are real patients we could not
   * get hold of. The old name read like an opt-in mailing list, and on the
   * strength of it the CTO came close to deleting the lane, which would have
   * stranded every one of them. The label has to state the operational fact, so
   * it names both the state and the cause.
   */
  unreachable: 'Unreachable — no answer',
  closed_lost: 'Lost',
  // NOT "Duplicate" alone, and not "Closed": the lane has to say BOTH that the
  // card is finished and WHY, because "Duplicate" beside "Won" and "Lost" reads
  // as a third outcome of the sale rather than as a bookkeeping close. The
  // card's own `[Duplicate]` marker (duplicateSurface.tsx) is the at-a-glance
  // cue; this is the lane it lives in.
  closed_duplicate: 'Merged duplicate',
};

/**
 * ══════════════════════════════════════════════════════════════════════════
 * THE OLD NAMES, STILL READABLE — the mirror of clinic-service's
 * LEGACY_STAGE_REMAP, for text the restructure could NOT rewrite.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * The server remapped every `Deal.stage` in place, so the BOARD only ever sees
 * the new vocabulary and nothing here needs a transition period. Audit rows are
 * the exception, and they are append-only by design: a `stage_change` entry
 * written before the cutover still says
 * `"eligibility_in_progress -> awaiting_clinical_review"` and always will.
 *
 * WITHOUT THIS MAP THOSE ROWS SILENTLY DE-HUMANISE. `humaniseStageSummary`
 * (DealDrawer.tsx) renders a stage move by looking both ends up in STAGE_LABEL
 * and returns the RAW string when either misses — so the moment the two old
 * keys left that map, every historical stage move in every activity feed
 * reverted to snake_case machine text. Nothing errors, nothing is logged, and
 * the regression is spread across the one surface staff read during a call.
 *
 * The labels are the ones those stages actually carried, NOT the merged lane's
 * label: the row is a record of what happened at the time, and rewriting
 * history to say "Clinical review → Clinical review" would turn a real
 * transition into a no-op that never occurred.
 */
export const LEGACY_STAGE_LABEL: Readonly<Record<string, string>> = {
  eligibility_in_progress: 'Checking eligibility',
  awaiting_clinical_review: 'Clinical review',
  marketing_funnel: 'Marketing funnel',
};

/** The display label for a stage token off the wire, live vocabulary or retired. */
export function stageTokenLabel(token: string): string | undefined {
  return (STAGE_LABEL as Record<string, string>)[token] ?? LEGACY_STAGE_LABEL[token];
}

/** Entry-path tag labels ("show the option they took"). */
export const ENTRY_LABEL: Record<DealEntryPath, string> = {
  widget_form: 'Widget form',
  callback_button: 'Requested callback',
  portal_journey: 'Started online',
  staff_sql: 'SQL',
};

/**
 * The stages with no work left in them — the mirror of the server's
 * TERMINAL_STAGES.
 *
 * `closed_duplicate` joined on 2026-09-06 and it is load-bearing in three
 * places at once: the board card drops its stage-age urgency cue (a merged card
 * ageing in red is noise about a card nobody should be working), the drawer
 * stops offering "Send for admin review", and the clinician's
 * questionnaire-start affordance goes. Its only exit is the server's own undo.
 *
 * ⚠️ TERMINAL IS NOT THE SAME AS LOST — see LOSS_STAGES below. This list
 * answers "is there work left here"; it must never be used to answer "did we
 * lose this patient".
 */
export const TERMINAL_STAGES: readonly DealStage[] = ['closed_won', 'closed_lost', 'closed_duplicate'];

/**
 * ══════════════════════════════════════════════════════════════════════════
 * THE LOSS CONTRACT — the stages that mean WE LOST THIS PATIENT. The mirror of
 * clinic-service's LOSS_STAGES.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `closed_duplicate` IS DELIBERATELY ABSENT, and that absence is the feature. A
 * merged duplicate is a real patient who submitted twice; the journey that
 * mattered lives on the canonical card, and whatever happened there — won,
 * lost, still running — is the honest answer for that person. Counting the
 * shadow card as a loss double-counts the patient AND records a conversion as a
 * failure, on the one board number ("where are we losing patients") that
 * somebody is going to act on.
 *
 * `closed_won` is absent for the mirror-image reason: it is the other real
 * outcome, not a loss.
 *
 * EXISTS SO THE QUESTION HAS ONE ANSWER. Before it, every surface that wanted
 * "was this lost" wrote `stage === 'closed_lost'` inline and every new terminal
 * stage had to be argued about at each of those sites independently. A named
 * list is the thing a reviewer can check against the server's.
 */
export const LOSS_STAGES: readonly DealStage[] = ['closed_lost'];

/** True when this stage books the deal as a LOST patient — see LOSS_STAGES. */
export function isLostStage(stage: DealStage): boolean {
  return LOSS_STAGES.includes(stage);
}

/** Which role's queue a stage primarily is — drives the focus emphasis. */
export const STAGE_FOCUS: Partial<Record<DealStage, 'CLINICIAN' | 'ADMIN'>> = {
  callback_requested: 'CLINICIAN',
  /**
   * ADMIN. The CTO named both desks for the manual pull into review ("admins or
   * clinicians can then reach out to them"), but the ROUTINE work of this lane
   * — ringing somebody who did not finish paying — is a sales chase, and
   * pointing a clinician's emphasis at a queue with no clinical decision in it
   * would put the more expensive desk on the cheaper job. A clinician who wants
   * to pull a card in can still do it from the board; the emphasis only says
   * whose column this is by default.
   */
  warm_lead: 'ADMIN',
  awaiting_clinician_followup: 'CLINICIAN',
  /**
   * CLINICIAN, WHICH IS A CHANGE FOR HALF THIS LANE'S CARDS. The merge combined
   * an ADMIN-focused stage (`eligibility_in_progress` — a self-serve check in
   * flight, chased by admin) with a CLINICIAN-focused one
   * (`awaiting_clinical_review` — a paid order in the clinician's queue), and a
   * single column can only carry one emphasis.
   *
   * The server settles it: `clinical_review` means "a clinician owns this
   * questionnaire, whether or not the patient has paid yet". Emphasising it for
   * admin would point the wrong desk at a clinical queue. The unpaid cards that
   * lose their ADMIN emphasis are not left unattended — the rot cron is what
   * chases a stalled self-serve check, not an admin reading this column.
   */
  clinical_review: 'CLINICIAN',
  awaiting_admin_followup: 'ADMIN',
  // The clinician handed this over precisely BECAUSE only an admin may action it.
  pending_admin_review: 'ADMIN',
};

export type ManualMoveVia = 'stage-patch' | 'handover' | 'convert';

/**
 * The staff desks a move can be restricted to — the mirror of clinic-service's
 * `MoveRole`. Deliberately NOT `UserRole`: a PATIENT never makes a manual move,
 * and a table that could name one would be describing something the route does
 * not admit.
 */
export type MoveRole = 'CLINICIAN' | 'ADMIN';

export interface ManualTarget {
  to: DealStage;
  via: ManualMoveVia;
  requiresReason: boolean;
  /**
   * WHICH DESK MAY MAKE THIS MOVE (2026-09-22). **ABSENT means both** — which
   * is every move that existed before that date, so a restriction is opt-in
   * here exactly as it is on the server and can never be inherited by a
   * neighbouring entry.
   *
   * AFFORDANCE ONLY (R41): `patchStage` throws 403 whatever this table says.
   * Mirroring it is what stops the portal offering a row that can only fail.
   */
  roles?: readonly MoveRole[];
}

/** Mirror of the server's MANUAL_TARGETS — keep in lock-step with deal-stages.ts. */
export const MANUAL_TARGETS: Readonly<Record<DealStage, readonly ManualTarget[]>> = {
  mql: [
    { to: 'callback_requested', via: 'convert', requiresReason: false },
    { to: 'closed_lost', via: 'stage-patch', requiresReason: true },
  ],
  /**
   * THE LEAD LANE (2026-09-07). The middle entry is THE MANUAL PULL INTO
   * REVIEW — the CTO's "get real intention, and approve" — and it is the reason
   * the authorisation gate is a default route rather than a wall. Rendering it
   * here is what makes the path REACHABLE: the status map and the board drag
   * both build their targets from this table, so without the entry the server
   * would accept a move nobody could ask for.
   *
   * `requiresReason: true` on it is the one difference from the otherwise
   * identical `unreachable → clinical_review` revive, and it must be mirrored
   * exactly or the ConfirmDialog would let a clinician pull a card in with no
   * note and the server would 400 them. Pulling a lead in puts the clinic back
   * on the hook for an approval fee; the reason IS the intention record.
   */
  warm_lead: [
    { to: 'callback_requested', via: 'convert', requiresReason: false },
    { to: 'clinical_review', via: 'stage-patch', requiresReason: true },
    { to: 'closed_lost', via: 'stage-patch', requiresReason: true },
  ],
  callback_requested: [
    { to: 'awaiting_admin_followup', via: 'handover', requiresReason: true },
    { to: 'closed_lost', via: 'stage-patch', requiresReason: true },
  ],
  // The merged clinical lane (2026-09-05). These are `eligibility_in_progress`'s
  // exits, kept verbatim — ring the patient about the check that has stalled, or
  // close the lead who has said no. A PAID card in this stage is refused BOTH of
  // them by the server (409), which is the `awaiting_clinical_review` half of
  // the merge; `movesFor()` below is how this mirror greys them out rather than
  // offering a drag that bounces.
  clinical_review: [
    { to: 'callback_requested', via: 'convert', requiresReason: false },
    { to: 'closed_lost', via: 'stage-patch', requiresReason: true },
  ],
  // Plan sent (2026-08-26): the move IN is server-automatic (the handover
  // hook); the manual moves OUT mirror clinical_review — ring the patient about
  // their unpaid plan, or close it lost with a reason.
  //
  // …AND, SINCE 2026-09-22, AN ADMIN MAY MARK THEM UNREACHABLE. This is the
  // lane the blocker was in: 68 cards whose patients do not answer the phone,
  // with no way to say so — `unreachable` is the last column on the board, off
  // the right edge of the scroller, so it could not be dragged to either.
  // UNGATED here (unlike the two admin call rounds below): no call round runs
  // on this lane, which is the same reason its closed_lost exit is only
  // reason-gated.
  plan_sent: [
    { to: 'callback_requested', via: 'convert', requiresReason: false },
    { to: 'unreachable', via: 'stage-patch', requiresReason: false, roles: ['ADMIN'] },
    { to: 'closed_lost', via: 'stage-patch', requiresReason: true },
  ],
  awaiting_clinician_followup: [
    { to: 'awaiting_admin_followup', via: 'handover', requiresReason: true },
    { to: 'closed_lost', via: 'stage-patch', requiresReason: true },
  ],
  // The unreachable exit (2026-09-22) is ADMIN-only like the others, and from
  // THIS lane the server also applies the §9 attempts gate — a move here makes
  // the same "we could not reach them" claim a closed_lost with reason
  // `unreachable` makes, so it is refused under three logged attempts. That is
  // a 409 the operator reads, not something this table can express: the gate is
  // about the card's call history, not about the stage.
  awaiting_admin_followup: [
    { to: 'awaiting_clinician_followup', via: 'handover', requiresReason: true },
    { to: 'unreachable', via: 'stage-patch', requiresReason: false, roles: ['ADMIN'] },
    { to: 'closed_lost', via: 'stage-patch', requiresReason: true },
  ],
  // The move IN is owned by the dedicated pending-admin-review endpoint, so this
  // entry describes the moves OUT: once the admin has actioned it they hand the
  // deal back or close it lost — both reason-gated, so the stage is never a
  // dead end.
  pending_admin_review: [
    { to: 'awaiting_clinician_followup', via: 'handover', requiresReason: true },
    { to: 'unreachable', via: 'stage-patch', requiresReason: false, roles: ['ADMIN'] },
    { to: 'closed_lost', via: 'stage-patch', requiresReason: true },
  ],
  closed_won: [],
  // Renamed from `marketing_funnel` (2026-09-05); all three exits survive the
  // rename, with the self-serve revive re-pointed at the merged clinical lane.
  // A revived card has never been authorised, so the padlock below cannot bite
  // on the target. Since 2026-09-22 the lane also has a manual way IN (an
  // ADMIN, from the three lanes above) — the revive out of it is unchanged and
  // stays open to BOTH desks with no reason, so a patient who calls back is one
  // click from a clinician.
  unreachable: [
    { to: 'callback_requested', via: 'convert', requiresReason: false }, // revive → FRESH ticket
    { to: 'clinical_review', via: 'stage-patch', requiresReason: false }, // revive to self-serve
    { to: 'closed_lost', via: 'stage-patch', requiresReason: true },
  ],
  closed_lost: [],
  /**
   * NO MANUAL EXIT, mirroring the server (`MANUAL_TARGETS.closed_duplicate` is
   * empty there too). A merged card is not un-merged by dragging it somewhere
   * else — the only way back is the mark's own undo (`revert: true` on
   * clinic-service's mark-duplicate route), which restores the stage the card
   * was banked from. Offering "Move to …" here would let staff strand a card on
   * a working lane while it still points at a canonical, which is exactly the
   * half-marked state the duplicate mark exists to avoid.
   */
  closed_duplicate: [],
};

/**
 * ══════════════════════════════════════════════════════════════════════════
 * THE MERGE'S ONE RULE — true when this card has an AUTHORISED card behind it
 * and is sitting in `clinical_review` waiting on the clinician's approve/reject.
 * The mirror of clinic-service's `isAwaitingClinicalDecision`.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * WHY A ROW READ AND NOT A STAGE READ. The two stages the merge combined had
 * OPPOSITE manual rules: `eligibility_in_progress` accepted manual moves,
 * `awaiting_clinical_review` accepted none at all, because dragging a PAID card
 * out of the review queue loses a sale the server can then never put back
 * (order-sync's `approved` only moves an OPEN deal, so a card dragged to
 * `closed_lost` stays lost while the patient's money and the clinician's
 * approval both say otherwise). Both rules had to survive the merge, so the
 * server keyed them on the fact that actually differed.
 *
 * ⚠️ THAT FACT WAS `orderCaseRef` UNTIL 2026-09-07, AND THE AUTHORISATION GATE
 * MADE IT EXACTLY BACKWARDS. `orderCaseRef` is stamped by the `checkout` arm of
 * the order-sync ingress, and after the gate `checkout` is precisely the event
 * that means NO card was authorised. So this predicate — which drives
 * `movesFor` below, and therefore every affordance the board offers — greyed
 * out all three exits on the one card the gate's escape hatch exists for: a
 * warm lead an admin pulled into review to "get real intention", carrying an
 * `orderCaseRef` from its own abandoned checkout. The server accepts those
 * moves; the UI refused to offer them.
 *
 * `holdAuthorisedAt` is the fact that means money: the instant orders-service
 * reported a HELD (or captured) `month1_preauth`. One writer on the server, and
 * it is on the wire specifically so this mirror can ask the SAME question the
 * 409 does. A mirror keyed on a different column is worse than no mirror.
 *
 * `orderCaseRef` keeps its own job on this card — the staff-facing "ORD-…"
 * reference, the Payments/Review tab key — and it is still NOT RELIABLY SET in
 * general (see the long note on `markOrderLink` in DealDrawer.tsx: the ingress
 * bails on a deal that was TERMINAL or PARKED when checkout landed). Neither
 * fact bears on this predicate any more.
 *
 * AFFORDANCE ONLY (R41). The server returns 409 on the manual exits; this is
 * how the portal greys them out instead of offering a drag that bounces.
 */
export function isAwaitingClinicalDecision(card: Pick<DealCard, 'stage' | 'holdAuthorisedAt'>): boolean {
  return card.stage === 'clinical_review' && card.holdAuthorisedAt != null;
}

/**
 * The manual moves to OFFER for a card — MANUAL_TARGETS filtered by the row
 * rule above.
 *
 * ⚠️ PREFER THIS OVER READING `MANUAL_TARGETS` DIRECTLY. Since the merge, the
 * stage alone no longer decides what a card may do: an AUTHORISED
 * `clinical_review` card has the table's exits and is refused all of them. A caller that indexes the
 * map itself gets an affordance the server rejects, which is the exact failure
 * the merge could have introduced — a padlocked queue quietly becoming
 * draggable.
 */
export function movesFor(
  card: Pick<DealCard, 'stage' | 'holdAuthorisedAt'>,
  role: UserRole,
): readonly ManualTarget[] {
  if (isAwaitingClinicalDecision(card)) return [];
  const moves = MANUAL_TARGETS[card.stage] ?? [];
  // ⚠️ THE ROLE IS REQUIRED, NOT OPTIONAL, AND THAT IS THE POINT (2026-09-22).
  // An optional parameter would let a new call site forget it and silently
  // offer an ADMIN-only move to a clinician — a control that can only ever
  // produce a 403. Making it required turns that into a compile error.
  //
  // It also fails CLOSED: an unrestricted table entry is offered to everyone,
  // and a restricted one only to the desks it names, so the worst a mistake
  // here can do is hide an action (visible, reported in a minute) rather than
  // offer one the server refuses.
  return moves.filter((t) => !t.roles || (t.roles as readonly string[]).includes(role));
}

/** Look up one manual move; undefined = not legal (grey it out / refuse the drop). */
export function manualTarget(from: DealStage, to: DealStage): ManualTarget | undefined {
  return (MANUAL_TARGETS[from] ?? []).find((t) => t.to === to);
}

/**
 * §9 three-attempt call workflow (UX Wave 7B) — the mirror of clinic-service's
 * REQUIRED_ROUND_ATTEMPTS. AFFORDANCE ONLY: the server-computed
 * `attemptsThisRound`/`roundOwner` on each card are the truth (the chip, the
 * composer's "attempt N of 3" prompt and the handover-first CTA all render
 * from them), and the closed_lost gate is enforced server-side (409 — the UI
 * surfaces the server's message, it never pre-blocks).
 */
export const REQUIRED_ROUND_ATTEMPTS = 3;

/**
 * ══════════════════════════════════════════════════════════════════════════
 * THE WARM-LEAD CALLING LANE (2026-09-08, CTO) — the client mirror.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * The CTO: *"Occasionally we should provide them the ability to call the warm
 * leads … It won't be a primary workflow."*
 *
 * ⚠️ IT READS THE CARD'S OWN `roundOwner`, NOT A LIST OF STAGE NAMES, and that
 * is the whole reason this mirror cannot drift. `roundOwner` is SERVER TRUTH —
 * clinic-service computes it with the very `roundOwnerFor()` that its
 * `LEAD_CALL_STAGES` is derived from — so this predicate is not a second copy
 * of the rule, it is a read of the first one. A stage list here would need
 * editing every time the server's vocabulary moved, and the failure mode is
 * silent: an action offered on a card the server 409s, or (worse) an action
 * MISSING from a lane staff were told they could work.
 *
 * It is also what makes `warm_lead` (branch feat/lead-until-authorised) light
 * this control up on merge with no edit to this file: that stage has no round
 * owner, so the server sends `roundOwner: null` for it and the predicate is
 * already true.
 *
 * AFFORDANCE ONLY (R41). The server refuses a lead call on an owned round with
 * a 409 whatever this returns.
 *
 * ⚠️ FALLS BACK TO FALSE ON AN OLDER SERVER. `roundOwner` is an additive
 * optional field: `undefined` means "this clinic-service does not send it", and
 * a missing field must never be read as "no round is active" — that would offer
 * the lead-call action on every card including the ones somebody is mid-round
 * on. Absent = hide the control; the drawer's ordinary Log-activity composer is
 * still there, exactly as it was before this feature.
 */
export function isLeadCallCard(
  card: Pick<DealCard, 'stage' | 'holdAuthorisedAt' | 'roundOwner'>,
): boolean {
  if (TERMINAL_STAGES.includes(card.stage)) return false;
  // ⚠️ THE PAID-CARD GUARD, AND IT IS A MONEY RULE (2026-09-08). A card that
  // reached checkout and is waiting on the clinician's approve/reject is in this
  // lane by the derivation above — it is open, and nobody is assigned to ring it
  // — and it must NOT be. The dialog's "not interested" route closes a lead as
  // lost, and doing that to a paid, undecided sale loses it outright:
  // `approved` only moves an OPEN deal, so nothing would put the card back. The
  // server refuses it with the same `assertNotAwaitingClinicalDecision` the two
  // manual channels use; this hides the row so a desk is never offered it.
  //
  // Reusing `isAwaitingClinicalDecision` rather than restating the rule is the
  // point: `movesFor` already reads it for exactly this purpose, and a paid
  // review card must offer the SAME nothing here that it offers there.
  //
  // ⚠️ KEYED ON `holdAuthorisedAt`, NOT `orderCaseRef` (2026-09-08 merge with the
  // authorisation gate). The gate re-keyed `isAwaitingClinicalDecision` when it
  // landed — merely reaching checkout now makes a `warm_lead`, and only an
  // AUTHORISED card sits in `clinical_review`. Left on the old key this would
  // still typecheck against a wider Pick and quietly guard nothing.
  if (isAwaitingClinicalDecision(card)) return false;
  return card.roundOwner === null;
}
